import { Flex, Heading, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { FileTextIcon } from 'lucide-react';
import { FC, MouseEvent } from 'react';
import { ComplianceSuggestion } from './types';

interface FolderSuggestionsCardProps {
  suggestions: ComplianceSuggestion[];
  /**
   * Called when a folder suggestion's "Ignore" is clicked, with the suggestion
   * to hide. The page builds the mutation variables (folder validation id +
   * document discriminators) from it. Omitted when ignoring isn't available.
   */
  onIgnore?: (suggestion: ComplianceSuggestion) => void;
  /**
   * Called when an already-ignored folder suggestion's "Undo" is clicked, with
   * the suggestion to un-hide (`isHidden: false`). Omitted when ignoring isn't
   * available.
   */
  onUndoIgnore?: (suggestion: ComplianceSuggestion) => void;
  /** When true, the "Ignore"/"Undo" affordances show a spinner and are disabled. */
  isIgnoring?: boolean;
}

/**
 * Amber/warning card listing folder-level compliance suggestions for the agent
 * (e.g. "Missing lead based paint addenda"). Renders nothing when there are no
 * suggestions.
 *
 * When `onIgnore` is provided each row renders an "Ignore" affordance that calls
 * `onIgnore(suggestion)`; the page builds the mutation variables from the
 * suggestion's folder validation id + document discriminators.
 */
export const FolderSuggestionsCard: FC<FolderSuggestionsCardProps> = ({
  suggestions,
  onIgnore,
  onUndoIgnore,
  isIgnoring = false,
}) => {
  if (suggestions.length === 0) {
    return null;
  }

  const heading =
    suggestions.length === 1
      ? '1 folder suggestion'
      : `${suggestions.length} folder suggestions`;

  return (
    <VStack
      align="stretch"
      bg="compliance.validation.warning.bg"
      borderColor="compliance.validation.warning.border"
      borderRadius="md"
      borderWidth={1}
      p={4}
      spacing={2}
    >
      <Heading
        color="compliance.status.warning"
        fontSize="md"
        fontWeight="semibold"
        size="headline"
      >
        {heading}
      </Heading>

      <VStack align="stretch" spacing={2}>
        {suggestions.map((suggestion, index) => {
          const isIgnored = suggestion.isIgnored === true;

          const handleIgnore = (event: MouseEvent): void => {
            event.stopPropagation();
            if (!isIgnoring) {
              onIgnore?.(suggestion);
            }
          };

          const handleUndoIgnore = (event: MouseEvent): void => {
            event.stopPropagation();
            if (!isIgnoring) {
              onUndoIgnore?.(suggestion);
            }
          };

          const showAction = isIgnored
            ? onUndoIgnore != null
            : onIgnore != null;

          return (
            <Flex
              key={suggestion.ruleUuid ?? `folder-suggestion-${index}`}
              align="flex-start"
              gap={2}
              opacity={isIgnored ? 0.6 : undefined}
            >
              <Icon
                as={FileTextIcon}
                boxSize="20px"
                color={
                  isIgnored
                    ? 'compliance.text.muted'
                    : 'compliance.status.warning'
                }
                flexShrink={0}
                mt="2px"
              />
              <Flex direction="column" gap={1} minW={0}>
                <Text
                  color={
                    isIgnored
                      ? 'compliance.text.muted'
                      : 'compliance.text.primary'
                  }
                  fontSize="sm"
                  textDecoration={isIgnored ? 'line-through' : undefined}
                >
                  {suggestion.message}
                </Text>
                {showAction &&
                  (isIgnoring ? (
                    <Flex
                      align="center"
                      color="whiteAlpha.600"
                      fontSize="sm"
                      gap={1.5}
                    >
                      <Spinner size="xs" />
                      <Text>{isIgnored ? 'Undoing' : 'Ignoring'}</Text>
                    </Flex>
                  ) : isIgnored ? (
                    <Text
                      _hover={{ color: 'compliance.text.secondary' }}
                      aria-label="Undo ignore suggestion"
                      as="button"
                      color="whiteAlpha.600"
                      cursor="pointer"
                      fontSize="sm"
                      textAlign="left"
                      textDecoration="underline"
                      type="button"
                      onClick={handleUndoIgnore}
                    >
                      Undo
                    </Text>
                  ) : (
                    <Text
                      _hover={{ color: 'compliance.text.secondary' }}
                      aria-label="Ignore suggestion"
                      as="button"
                      color="whiteAlpha.600"
                      cursor="pointer"
                      fontSize="sm"
                      textAlign="left"
                      textDecoration="underline"
                      type="button"
                      onClick={handleIgnore}
                    >
                      Ignore
                    </Text>
                  ))}
              </Flex>
            </Flex>
          );
        })}
      </VStack>
    </VStack>
  );
};
